import React, { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import {Helmet} from "react-helmet";
import { Box, Button, Container, List, ListItem, ListItemText, Paper, Stack, Typography, useTheme } from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import TaskIcon from '@mui/icons-material/Task';
import GavelIcon from '@mui/icons-material/Gavel';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import useNavigateHook from '../hooks/navigate.hook';
import { RootState } from '../store';
import FormModal from '../components/modals/form.modal';
import RegisterForm from '../components/forms/registration.form';
import LoginForm from '../components/forms/login.form';

const HowItWorkPage: React.FC = () => {

  const theme = useTheme();

  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const { doNavigate } = useNavigateHook();

  const [showLoginFormModal, setShowLoginFormModal] = useState(false);
  const [showRegisterFormModal, setShowRegisterFormModal] = useState(false);

  const stepContent = [
    {
      step: 1,
      highLightColor: '#155dfc',
      title: 'Register & Get Approved',
      icon: <PersonAddAlt1Icon sx={{ color: "#155dfc", fontSize: 28 }}/>,
      text: <>
        <Typography variant="body1" component="p">
          Create your account by providing your company details, including firm name, contact information, PAN, 
          and GSTIN. Choose your role - either as a Company (Seller) or Buyer.
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Fill in comprehensive company information" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Submit required documents for verification" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Wait for admin approval (usually within 24-48 hours)" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Receive email notification once approved" />
          </ListItem>
        </List>
      </>
    },
    {
      step: 2,
      highLightColor: theme.palette.primary.main,
      title: 'Create or Apply to Auction',
      icon: <TaskIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }}/>,
      text: <>
        <Typography variant="body1" component="p" color={theme.palette.primary.main}>
          For Sellers (Companies):
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Create an auction listing with product details, quantity, and images" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Specify material lifting details and validity period" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="List any required documents from buyers" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Submit for admin approval - admin will set start/end times and starting price" />
          </ListItem>
        </List>
        <Typography variant="body1" component="p" color={theme.palette.primary.main} mt={2}>
          For Buyers:
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Browse available approved auctions" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Review auction details and requirements" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Apply to participate by uploading required documents" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Wait for application approval from admin" />
          </ListItem>
        </List>
      </>
    },
    {
      step: 3,
      highLightColor: '#9810fa',
      title: 'Participate in Live Auction',
      icon: <GavelIcon sx={{ color: "#9810fa", fontSize: 28 }}/>,
      text: <>
        <Typography variant="body1" component="p">
          Once your application is approved, participate in real-time bidding. Our platform features advanced bidding technology to ensure fairness and efficiency.
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary={<><Typography component="span" fontWeight="600">Manual Bidding: </Typography>Place bids manually during the auction</>} />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary={<><Typography component="span" fontWeight="600">Auto-Bid: </Typography>Set your maximum price and let the system bid automatically</>} />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary={<><Typography component="span" fontWeight="600">Real-Time Updates: </Typography>See bids instantly as they happen</>} />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary={<><Typography component="span" fontWeight="600">Anti-Sniping: </Typography>Auction extends by 3 minutes if a bid is placed in the last 3 minutes</>} />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary={<><Typography component="span" fontWeight="600">Complete Transparency: </Typography>View full bid history at any time</>} />
          </ListItem>
        </List>
      </>
    },
    {
      step: 4,
      highLightColor: '#f54a00',
      title: 'Win & Complete Transaction',
      icon: <WorkspacePremiumIcon sx={{ color: "#f54a00", fontSize: 28 }}/>,
      text: <>
        <Typography variant="body1" component="p">
          When the auction ends, the highest bidder wins. Complete your transaction with confidence knowing all details are recorded and transparent.
        </Typography>
        <List sx={{ listStyleType: "disc", pl: 4 }}>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Highest bidder is declared the winner" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Seller and buyer receive notifications" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Complete the transaction offline as per agreed terms" />
          </ListItem>
          <ListItem sx={{ display: "list-item", py: 0 }}>
            <ListItemText primary="Access full bid history and auction records anytime" />
          </ListItem>
        </List>
      </>
    },
  ];

  const openAuthForm = () => {
    setShowRegisterFormModal(true);
  };

  const openLoginFormModal = () => {
    setShowRegisterFormModal(false);
    setShowLoginFormModal(true);
  }

  const openRegistrationFormModal = () => {
    setShowRegisterFormModal(true);
    setShowLoginFormModal(false);
  }

  const closeAuthForm = () => {
    setShowRegisterFormModal(false);
    setShowLoginFormModal(false);
  };

  const stepCard = (stepNumber: number, title: string, text: ReactNode | null, icon: ReactNode | null, highLightColor = theme.palette.primary.main ) => {
    return <Paper sx={{ p: 4, borderLeft: `6px solid ${highLightColor}`, }}>
      <Stack gap={3} flexDirection="row">
        <Box>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              backgroundColor: highLightColor,
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              fontWeight: 600,
            }}
          >{stepNumber}</Box>
        </Box>
        <Stack gap={2} direction="column">
          <Stack gap={3} flexDirection="row">
            {icon}
            <Typography variant="h3" fontWeight={600} mb={0}>{title}</Typography>
          </Stack>
          <Box>{text}</Box>
        </Stack>
      </Stack>
    </Paper>
  }

  return (
    <>
      <Helmet>
        <title>Auction Process - Scrap E-Auction Online Scrap Metal Auction Platform</title>
        <meta name="description" content="Learn how our steel scrap auction platform works" />
      </Helmet>
      <Box sx={{ backgroundColor: '#fbf9fa'}}>
        <Container sx={{ py: 4, bgcolor: '#fbf9fa' }}>
          <Stack gap={5} direction="column">
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="h1" gutterBottom sx={{ fontWeight: '600' }}>
                Auction Process - Transparent & Simplified
              </Typography>
              <Typography variant="body1" gutterBottom color="textSecondary">
                Simple and transparent online scrap auctions
              </Typography>
            </Box>
            <Paper sx={{ p: 4 }}>
              <Typography variant="body1" component="p" mb={2}>
                <Typography component="span" color='#000' fontWeight={600}>Welcome to scrapauction.in</Typography> its provide you simple and transparent online scrap auctions. For participation in e-auction for particular auction or lot. The final bidder shall be authorized by seller as mentioned below.
              </Typography>
              <Typography variant="body1" component="p">
                You can bid by two ways standard bid and auto bid.
              </Typography>
            </Paper>
            <Stack direction="row" gap={4} justifyContent="space-between">
              <Paper sx={{ p: 4, flex: 1 }}>
                <Typography variant="h3" fontWeight={600} mb={2}>Standard bid</Typography>
                <Typography variant="body1" component="p">
                  Standard bid is bid by buyer, in this you can challenge to other buyers, with in a time period. The highest bidder will be taken into consideration.
                </Typography>
              </Paper>
              <Paper sx={{ p: 4, flex: 1 }}>
                <Typography variant="h3" fontWeight={600} mb={2}>Auto bid</Typography>
                <Typography variant="body1" component="p">
                  Auto bid is a proxy bidding features. Auto bid facility is provided for bidders intending to place 
                  a maximum value for a lot/lots. Auto bid is not a confirmed bid. It is only the maximum selling 
                  amount set by the bidder to enable the auction software to place bids on his behalf, whenever he 
                  is out bided, up to the selling set by him. Once auto bid is set, the auction software will consider 
                  the selling amount for the next possible bid only, depending on the highest bid prevailing at 
                  that point of time and the increment amount prescribed for that particular lot. 
                  Bidders may please note that in the event of a manual bid amount matching that of auto bid, 
                  the manual bid will prevail and be considered.
                </Typography>
              </Paper>
            </Stack>
            {stepContent.map((content) => (
              stepCard(content.step, content.title, content.text, content.icon, content.highLightColor)
            ))}
            <Paper
              sx={{
                p: 4,
                background: 'linear-gradient(90deg, #2563eb 0%, #9333ea 100%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Typography variant="h3" color="#ffffff" fontWeight={600} mb={2}>Ready to Start Trading?</Typography>
              <Typography variant="body1" color="#ffffff">
                {`Join hundreds of verified buyers and sellers on India's most trusted platform`}
              </Typography>
              <Stack gap={3} direction="row">
                {!isAuthenticated && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={openAuthForm}
                  >
                    Register Now
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => doNavigate('/auctions.html')}
                  sx={{
                    borderColor: '#ffffff',
                    color: '#ffffff'
                  }}
                >
                  Browse Auctions
                </Button>
              </Stack>
            </Paper>
          </Stack>
        </Container>
      </Box>
      {showRegisterFormModal && (
        <FormModal
          open={showRegisterFormModal}
          onClose={() => setShowRegisterFormModal(false)}
          size="xs"
        >
          <RegisterForm onSuccess={closeAuthForm} loginCallback={openLoginFormModal} />
        </FormModal>
      )}
      {showLoginFormModal && (
        <FormModal open={showLoginFormModal} onClose={() => setShowLoginFormModal(false)} size="xs">
          <LoginForm onSuccess={closeAuthForm} registerCallback={openRegistrationFormModal} />
        </FormModal>
      )}
    </>
  );
};

export default HowItWorkPage;
